// ---------------------------------------------------------------------------
// pleasantSlot — the highway rules for the Pleasant and Scenic cards.
//
// Extracted from a useMemo in Index.tsx so they can be tested at all. The
// numbers here were invisible to every test suite for weeks, because the
// fixtures in the taste tests set motorwayMeters to 0 and that is the only
// field these read.
//
// BASELINE (established July 31): the build that shipped to the App Store on
// 13 July is `d488d39` (10 July). In that build:
//
//   • Pleasant barred any route with >= 15% motorway.
//   • Scenic had NO highway gate at all.
//
// So a mixed route — interstate through the dull middle, back roads where it
// is pretty — was rejected by Pleasant and WELCOMED by Scenic. It had a home.
// That is how New York → Boston surfaced "Takes CT 17 and MA 16 midway
// through" at 5h 2m on the Scenic card.
//
// On 30 July the Scenic gate arrived ("no highways on scenic unless epic").
// The mixed route was then refused by both slots and vanished, leaving only
// the extreme 351-minute zero-highway option. That is the regression.
// ---------------------------------------------------------------------------

/** Pleasant's bar, exactly as shipped on 13 July. A route riding real
 *  motorway for a sixth of its length is a highway route, not a pleasant one.
 *  Deliberately NOT trip-scaled: on a long trip the mixed route belongs in
 *  Scenic, which is where the shipped build put it. */
export const MOTORWAY_PLEASANT_MAX_PCT = 15;

// --- Scenic ---------------------------------------------------------------
// Morgan, 30 July: "this is a NO — no highways on scenic route unless
// designated epic." True of the half-hour drive she was looking at.
// Morgan, 31 July: New York → Boston is "a disaster" because the good mixed
// route is gone. Also true.
//
// Both are right, at different trip lengths — which is the thing she named:
// "different rules for different length trips". So the Scenic bar scales.
// Short trips keep the 30 July rule as written; long trips return to the
// shipped behaviour, where Scenic accepted whatever was nicest.

/** Under this, a trip is short and Scenic holds the strict 30 July line. */
export const SCENIC_SHORT_TRIP_MINUTES = 45;
/** Miles off the motorway that make a route "not a highway route" regardless
 *  of ratio (Morgan, Aug 9: Route 66 at 18h is fine as Scenic, "but we need a
 *  shorter nice one"). 80 km ~ 50 miles of smaller roads is a different drive
 *  at any trip length. Below this the percentage rule above governs unchanged,
 *  so city trips behave exactly as they did. */
export const OFF_MOTORWAY_PLEASANT_MIN_M = 80_000;

/** At or beyond this, interstate through the middle is unavoidable and a
 *  mixed route is the only honest long way. */
export const SCENIC_LONG_TRIP_MINUTES = 150;
/** The strict bar — a third, per CORE GOALS rule 2. */
export const SCENIC_MOTORWAY_PCT_SHORT = 33;
/** Effectively unbarred, matching the shipped build. */
export const SCENIC_MOTORWAY_PCT_LONG = 100;

/** Motorway-class share of a route, 0..100. */
export function motorwayPct(route: {
  motorwayMeters?: number | null;
  distance?: number | null;
}): number {
  const m = route.motorwayMeters ?? 0;
  const d = route.distance || 1;
  return (m / d) * 100;
}

/** True when the route may never wear "Pleasant". Brief motorway contact — a
 *  ramp, a bridge — is fine; a sixth of the drive is not. */
export function isHighwayHeavy(route: {
  motorwayMeters?: number | null;
  distance?: number | null;
}): boolean {
  const pct = motorwayPct(route);
  if (pct < MOTORWAY_PLEASANT_MAX_PCT) return false;
  // ROAD-TRIP ESCAPE (Morgan, Aug 9, 1am, pointing at the San Jose → Flagstaff
  // cards: "THIS is the one that should be pleasant").
  //
  // "A sixth of the drive" is a city rule and it is right in a city: a sixth of
  // a 5-mile trip is 0.8 miles of motorway. A sixth of a 750-mile trip is 125
  // miles — and the route it disqualified still spends ONE HUNDRED AND EIGHTEEN
  // MILES off the interstate. On any cross-country drive every viable candidate
  // clears a sixth, so this rule made a Pleasant card structurally impossible
  // there: the display kept dropping the middle route the engine had correctly
  // classified as Pleasant.
  //
  // What makes a route "not a highway route" is how far it gets you OFF the big
  // road, not the ratio. Fifty miles of smaller roads is a different drive at
  // any trip length; below that the percentage rule stands exactly as before.
  const offMotorwayM =
    (route.distance ?? 0) * (1 - Math.min(100, Math.max(0, pct)) / 100);
  return offMotorwayM < OFF_MOTORWAY_PLEASANT_MIN_M;
}

/** The Scenic motorway bar for a trip of this length, in percent. */
export function scenicMotorwayBar(tripSeconds: number | null | undefined): number {
  const mins = (tripSeconds ?? 0) / 60;
  if (!Number.isFinite(mins) || mins <= SCENIC_SHORT_TRIP_MINUTES) {
    return SCENIC_MOTORWAY_PCT_SHORT;
  }
  if (mins >= SCENIC_LONG_TRIP_MINUTES) return SCENIC_MOTORWAY_PCT_LONG;
  const t =
    (mins - SCENIC_SHORT_TRIP_MINUTES) /
    (SCENIC_LONG_TRIP_MINUTES - SCENIC_SHORT_TRIP_MINUTES);
  return (
    SCENIC_MOTORWAY_PCT_SHORT +
    t * (SCENIC_MOTORWAY_PCT_LONG - SCENIC_MOTORWAY_PCT_SHORT)
  );
}


/**
 * THE DISPLAYED FAST CARD MUST BE THE QUICKEST DISPLAYED ROUTE
 * (Morgan, Aug 23 — Asheville → Hendersonville).
 *
 * The engine seats its Direct slot on the pool minimum, but the display builds
 * its own card set afterwards and injects the Google direct route at that
 * point. It then took whichever card carried `classification === "fastest"` as
 * its Fast anchor — a label decided before that injection existed. Measured:
 *
 *   [slots] fastest=google-direct/41m · pleasant=valhalla-adventure/40m
 *
 * Fast, one minute slower than Pleasant, on screen. The engine-side
 * [fast-invariant] cannot see this: it runs inside classifyThreeCards, and
 * google-direct never passes through there.
 *
 * So the anchor is chosen by duration over the final card set, after every
 * display-layer injection. No routing preference or score is consulted — this
 * is card assembly, and "Fast" is a claim about time.
 *
 * Ties keep the incumbent: when the classification-fastest card is already
 * among the quickest, nothing moves.
 */
export function pickDisplayFastAnchor<T extends {
  classification?: string;
  route: { duration: number };
}>(cards: T[]): T | null {
  if (cards.length === 0) return null;
  const quickest = cards.reduce((a, b) => (a.route.duration <= b.route.duration ? a : b));
  const labelled = cards.find((c) => c.classification === "fastest");
  if (labelled && labelled.route.duration <= quickest.route.duration) return labelled;
  return quickest;
}


/** Morgan, Aug 9: the Alternative "has to be within a few points of the other
 *  scenic card — basically an alternative to scenic but not pleasant." */
export const ALTERNATIVE_SCENIC_POINT_GAP = 10;

/** Why no Alternative card was offered, when none was. `null` means the pick
 *  ran normally (it may still have returned nothing). */
export type AlternativeStandDown = "no-scenic-card" | "scenic-unmeasured" | null;

export interface AlternativePick<T> {
  pick: T | null;
  /** One entry per candidate that reached the scoring stage, for the `[alt]`
   *  confession — `source:+3`, `source:-14`, or `source:same-road-as-fastest`. */
  considered: string[];
  /** The Scenic card's scenic score, the yardstick everything is judged against. */
  scenicScore: number;
  standDown: AlternativeStandDown;
}

/**
 * THE ALTERNATIVE CARD'S PICK (Morgan, Aug 9: "we can make the fast route fast
 * & pleasant and have a scenic route and have an alternate route as well").
 *
 * Extracted from the useMemo in Index.tsx for the same reason everything else
 * in this file was: it could not be tested where it lived, and its only trace
 * in the wild is a console line.
 *
 * SAME ROAD, NOT SAME CLOCK (Morgan, Sep 23: "Delete the 2-minute rule. Add a
 * proper check while the card is choosing: 'is this the same road as the
 * fastest route? If so, skip it and try the next one.'").
 *
 * What the loop used to ask was `|duration - fastestDuration| <= 120` — the
 * Aug 2 rule copied in on Aug 9, carrying a threshold nobody set ("what is
 * this 2 min rule? insane - i never said that", Aug 21). It stood in for the
 * same-road question and got it wrong both ways: it deleted a genuinely
 * different road that happened to be about as quick as the highway, and it let
 * a slower copy of the fastest road through to be rejected after the loop had
 * ended, by which point no other candidate could take the slot.
 *
 * The caller supplies the predicates it already owns (rule-20 measurement,
 * source, the trip-scaled highway bar, rule 3's road identity) so this stays a
 * pure ranking step and the two layers cannot drift apart.
 */
export function pickAlternativeCard<T extends {
  route: { epicHighwayMeters?: number | null };
  features?: { highwayPct?: number | null } | null;
  scores?: { scenic?: number | null } | null;
}>(
  candidates: readonly T[],
  ctx: {
    scenicPick: T | null;
    pleasantPick: T | null;
    /** The displayed Fast anchor. Null when the card set has no Fast card, in
     *  which case there is no road to be identical to. */
    fastestRoute: T | null;
    isUnmeasured: (c: T) => boolean;
    sourceOf: (c: T) => string;
    scenicHighwayOK: (c: T) => boolean;
    sameRoad: (a: T, b: T) => boolean;
  },
): AlternativePick<T> {
  const { scenicPick, pleasantPick, fastestRoute } = ctx;
  if (!scenicPick) {
    return { pick: null, considered: [], scenicScore: 0, standDown: "no-scenic-card" };
  }
  const target = scenicPick.scores?.scenic ?? 0;
  // A SCORE THAT WAS NEVER COMPUTED IS NOT A ZERO (rule 20). scenicScore() is
  // built entirely from OSM features, so a card whose enrichment never finished
  // reports scenic=0 — on Briarcliff the CURATED River Road card read 0 while
  // the interstate read 26. Ranking "within a few points" against a score
  // nobody measured would rank this card on noise, so it stands down instead.
  if (target <= 0) {
    return { pick: null, considered: [], scenicScore: target, standDown: "scenic-unmeasured" };
  }
  const considered: string[] = [];
  let best: T | null = null;
  for (const c of candidates) {
    if (c === scenicPick || c === pleasantPick) continue;
    if (ctx.isUnmeasured(c)) continue;
    const src = ctx.sourceOf(c);
    // A DIRECT ENGINE ROUTE IS NEVER SCENIC (rule 16 — the source says what it is).
    if (src === "mapbox-direct" || src === "google-direct") continue;
    if (!ctx.scenicHighwayOK(c)) continue;
    // The same honesty bar the engine uses to drop a dishonest Scenic card.
    if ((c.features?.highwayPct ?? 0) >= 60 && (c.route.epicHighwayMeters ?? 0) <= 1000) continue;
    // Rule 3's one question, asked HERE, where a "no" still leaves the next
    // candidate a chance. The clock is not consulted.
    if (fastestRoute && ctx.sameRoad(c, fastestRoute)) {
      considered.push(`${src}:same-road-as-fastest`);
      continue;
    }
    if (ctx.sameRoad(c, scenicPick)) continue;
    const gap = target - (c.scores?.scenic ?? 0);
    considered.push(`${src}:${gap <= 0 ? "+" : "-"}${Math.abs(Math.round(gap))}`);
    if (gap > ALTERNATIVE_SCENIC_POINT_GAP) continue;
    if (!best || (c.scores?.scenic ?? 0) > (best.scores?.scenic ?? 0)) best = c;
  }
  return { pick: best, considered, scenicScore: target, standDown: null };
}


/**
 * WIDEN THE PLEASANT CANDIDATE SET TO WHAT THE ENGINE RANKED
 * (Morgan, Aug 23 — Stamford / San Francisco / Denver).
 *
 * The display re-derives its own Pleasant pick from `seated minus the Fast
 * anchor`. On a long trip that is two routes, and by construction the wrong
 * two: the direct route (a highway route at 40+ miles, correctly rejected) and
 * the Scenic long way (over the Pleasant budget, correctly rejected). The
 * engine meanwhile had ranked four or five calm, in-budget candidates:
 *
 *   Stamford  pool=10 → eligible=7 → calm=4    display saw 2
 *   SF        pool=11 → eligible=7 → calm=5    display saw 2
 *   Denver    pool=9  → eligible=8 → calm=5    display saw 2
 *
 * This returns the seated candidates followed by the ranked pool entries that
 * are not already seated. It applies NO filters of its own — the caller's time
 * cap, rule-20 measurement bar and isHighwayHeavy test run afterwards exactly
 * as before, and the winner is still chosen the same way. The only change is
 * what gets asked.
 *
 * STALENESS FAILS CLOSED (rule 18 — route generation races). The snapshot
 * carries the cards its own run produced; if none of them appears in what is
 * being displayed, the pool is from a different search and is dropped.
 */
export function mergeEnginePoolIntoPleasantCandidates<
  S extends { route: unknown },
  P extends { route: unknown },
>(
  seatedCandidates: S[],
  displayedCards: { route: unknown }[],
  snapshot: { pool: P[]; cards: { route: unknown }[] } | null | undefined,
): { candidates: (S | (P & { classification: "pleasant" }))[]; extras: (P & { classification: "pleasant" })[]; poolUsed: boolean } {
  const poolUsed =
    !!snapshot &&
    snapshot.pool.length > 0 &&
    snapshot.cards.some((r) => displayedCards.some((c) => c.route === r.route));
  if (!poolUsed) return { candidates: [...seatedCandidates], extras: [], poolUsed: false };
  const seen = new Set(displayedCards.map((c) => c.route));
  const extras = snapshot!.pool
    .filter((r) => !seen.has(r.route))
    .map((r) => ({ ...r, classification: "pleasant" as const }));
  return { candidates: [...seatedCandidates, ...extras], extras, poolUsed: true };
}
